/**
 * =============================================================================
 * ContentProtectionGate.tsx - Applicazione runtime delle protezioni contenuti
 * =============================================================================
 *
 * Componente "invisibile" da montare a livello root dell'app. Legge il livello
 * di protezione del tenant corrente e:
 *  1. Inietta uno <style> globale con le regole CSS necessarie
 *     (user-select, @media print, immagini non trascinabili).
 *  2. Registra listener globali su document per intercettare
 *     contextmenu / copy / dragstart / keydown (Ctrl/Cmd + C/S/P).
 *  3. Aggiunge classi sul <body> per consentire eventuali override puntuali
 *     (es. textarea/input devono restare selezionabili anche in L3).
 *
 * NOTA SUI PRIVILEGI: Gli amministratori bypassano totalmente queste protezioni.
 *
 * IMPORTANTE: tutti gli input e i contenteditable sono sempre esclusi dal
 * blocco di selezione/copia, altrimenti il portale diventerebbe inutilizzabile
 * (login, ricerca, editor admin, forum, ecc.).
 *
 * @version 1.1.0 - Aggiunto bypass per amministratori
 * =============================================================================
 */

import { useEffect } from "react";
import { useContentProtection } from "@/hooks/useContentProtection";
import { useAuth } from "@/hooks/useAuth";

/** Selettori che DEVONO restare interattivi anche con protezione massima. */
const INTERACTIVE_SELECTORS = [
  "input",
  "textarea",
  "select",
  "[contenteditable='true']",
  "[contenteditable='']",
  "[data-allow-copy]",
];

function isInteractiveTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  return INTERACTIVE_SELECTORS.some((sel) => target.closest(sel));
}

export function ContentProtectionGate() {
  const { flags, level } = useContentProtection();
  const { hasRoleLevel } = useAuth();
  
  // Verifica se l'utente ha privilegi da amministratore (livello 0)
  const isAdmin = hasRoleLevel("amministratore");

  useEffect(() => {
    // Se è amministratore, non applicare ALCUNA restrizione DOM.
    if (isAdmin) return;

    const body = document.body;
    // Classe utile per CSS condizionale e per leggere il livello dai DevTools.
    body.dataset.contentProtection = level;

    // Listener tasto destro.
    const onContextMenu = (e: MouseEvent) => {
      if (!flags.disableContextMenu) return;
      if (isInteractiveTarget(e.target)) return;
      e.preventDefault();
    };

    // Listener copia.
    const onCopy = (e: ClipboardEvent) => {
      if (!flags.disableCopy) return;
      if (isInteractiveTarget(e.target)) return;
      e.preventDefault();
    };

    // Listener drag (immagini).
    const onDragStart = (e: DragEvent) => {
      if (!flags.disableDrag) return;
      if (isInteractiveTarget(e.target)) return;
      e.preventDefault();
    };

    // Listener shortcut da tastiera.
    const onKeyDown = (e: KeyboardEvent) => {
      const mod = e.ctrlKey || e.metaKey;
      if (!mod) return;
      const key = e.key.toLowerCase();
      // Lascia sempre passare gli shortcut dentro input/textarea/editor.
      if (isInteractiveTarget(e.target)) return;

      if (flags.disableCopy && key === "c") {
        e.preventDefault();
      } else if (flags.disableSave && key === "s") {
        e.preventDefault();
      } else if (flags.disablePrint && key === "p") {
        e.preventDefault();
      }
    };

    document.addEventListener("contextmenu", onContextMenu);
    document.addEventListener("copy", onCopy);
    document.addEventListener("dragstart", onDragStart);
    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.removeEventListener("contextmenu", onContextMenu);
      document.removeEventListener("copy", onCopy);
      document.removeEventListener("dragstart", onDragStart);
      document.removeEventListener("keydown", onKeyDown);
      // Pulizia attributo dataset alla rimozione
      delete body.dataset.contentProtection;
    };
  }, [flags, level, isAdmin]);

  // Se l'utente è amministratore, non montare lo stile CSS restrittivo
  if (isAdmin) {
    return null;
  }

  // Stile globale dinamico in base ai flag.
  const css = buildProtectionCss(flags);

  return (
    <style
      data-content-protection-style
      // Iniezione diretta del CSS calcolato — è breve e dinamico in base al livello.
      dangerouslySetInnerHTML={{ __html: css }}
    />
  );
}

function buildProtectionCss(flags: ReturnType<typeof useContentProtection>["flags"]): string {
  const rules: string[] = [];

  if (flags.disableTextSelection) {
    // Disabilita selezione su body, ma riabilita su input/textarea/editor.
    rules.push(`
      body { -webkit-user-select: none; -ms-user-select: none; user-select: none; }
      input, textarea, select,
      [contenteditable='true'], [contenteditable=''],
      [data-allow-copy], [data-allow-copy] * {
        -webkit-user-select: text !important;
        -ms-user-select: text !important;
        user-select: text !important;
      }
    `);
  }

  if (flags.disableDrag) {
    rules.push(`
      img, svg, picture { -webkit-user-drag: none; user-drag: none; }
    `);
  }

  if (flags.disablePrint) {
    // Neutralizza l'output di stampa con un messaggio chiaro.
    rules.push(`
      @media print {
        body * { visibility: hidden !important; }
        body::before {
          content: "Stampa non consentita dal titolare del portale.";
          visibility: visible !important;
          display: block;
          padding: 2rem;
          font-family: system-ui, sans-serif;
          font-size: 14pt;
          text-align: center;
        }
      }
    `);
  }

  return rules.join("\n");
}

export default ContentProtectionGate;